import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  message,
  Card,
  Avatar,
  Tooltip,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import {
  SearchOutlined,
  UserAddOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  UserDeleteOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  UnlockOutlined,
  SwapOutlined,
  UsergroupDeleteOutlined,
  UserOutlined,
  CalendarOutlined,
  SafetyOutlined,
  CrownOutlined,
  SettingOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import {
  DELETE_MEMBER,
  ENABLE_MEMBER,
  FETCH_ALL_MEMBERS,
  TOGGLE_MEMBER_ROLE,
  UPDATE_PHONE_NUMBER,
} from "../../services/apiEndPoints";
import { IMember } from "../types/member.types";
import MemberInviteComponent from "./MemberInviteComponent";
import axios from "../../services/axiosInstance";

const { Title, Text } = Typography;

const STAT_CARDS = [
  {
    key: "total",
    label: "Total Members",
    icon: <TeamOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "admins",
    label: "Admins",
    icon: <SafetyCertificateOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
  {
    key: "verified",
    label: "Verified",
    icon: <CheckCircleOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "inactive",
    label: "Inactive",
    icon: <UserDeleteOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
  },
];

const MemberTableComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState<IMember[]>([]);
  const searchInput = useRef<InputRef>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteForm] = Form.useForm();
  const [phoneForm] = Form.useForm();
  const [editingMember, setEditingMember] = useState<IMember | null>(null);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const members = await axios.get(FETCH_ALL_MEMBERS);
      if (members?.data) setData(members.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const deleteMember = async (memberId: string) =>
    axios.delete(`${DELETE_MEMBER}/${memberId}`);

  const enableMember = async (memberId: string) =>
    axios.put(`${ENABLE_MEMBER}/${memberId}`, {});

  const handleDelete = useCallback(async (memberId: string) => {
    try {
      await deleteMember(memberId);
      message.success("Member deleted successfully");
      await fetchMembers();
    } catch {
      message.error("Failed to delete member");
    }
  }, []);

  const handleEnable = useCallback(async (memberId: string) => {
    try {
      await enableMember(memberId);
      message.success("Member enabled successfully");
      await fetchMembers();
    } catch {
      message.error("Failed to enable member");
    }
  }, []);

  const handleToggleRole = useCallback(
    async (memberId: string, role: string) => {
      try {
        await axios.put(`${TOGGLE_MEMBER_ROLE(memberId)}`, { role });
        message.success("Member role updated successfully");
        await fetchMembers();
      } catch {
        message.error("Failed to update member role");
      }
    },
    []
  );

  const openPhoneModal = useCallback((member: IMember) => {
    setEditingMember(member);
    phoneForm.setFieldsValue({ phoneNumber: member.phoneNumber || "" });
    setPhoneModalOpen(true);
  }, [phoneForm]);

  const handleUpdatePhone = useCallback(async () => {
    try {
      const values = await phoneForm.validateFields();
      setPhoneLoading(true);
      const phoneNumber = values.phoneNumber.trim();
      await axios.put(UPDATE_PHONE_NUMBER(editingMember!.memberId), { phoneNumber });
      message.success("Phone number updated successfully");
      setPhoneModalOpen(false);
      setEditingMember(null);
      phoneForm.resetFields();
      await fetchMembers();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      if (msg) {
        message.error(msg);
      }
    } finally {
      setPhoneLoading(false);
    }
  }, [phoneForm, editingMember]);

  const getColumnSearchProps = useCallback(
    (dataIndex: string) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters = () => {},
      }: any) => (
        <div style={{ padding: 8, width: 240 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            >
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                setSearchText("");
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value: any, record: IMember) =>
        record[dataIndex as keyof IMember]
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      render: (text: any) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text?.toString() || ""}
          />
        ) : (
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        ),
    }),
    [searchText, searchedColumn, handleSearch]
  );

  const filteredData = useMemo(() => {
    if (!globalSearch) return data;
    return data.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalSearch.toLowerCase())
    );
  }, [data, globalSearch]);

  const stats = useMemo(
    () => ({
      total: data.length,
      admins: data.filter((d) => d.role === "admin").length,
      verified: data.filter((d) => d.isVerified).length,
      inactive: data.filter((d) => !d.isActive).length,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        title: <><UserOutlined style={{ fontSize: 12 }} /> Member</>,
        dataIndex: "memberName",
        key: "memberName",
        ...getColumnSearchProps("memberName"),
        render: (name: string, record: IMember) => (
          <Space>
            <Avatar
              size={32}
              style={{
                background: record.isActive
                  ? "linear-gradient(135deg, #1677ff, #0ea5e9)"
                  : "#d1d5db",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </Avatar>
            <div>
              <Text strong style={{ display: "block", lineHeight: 1.3 }}>
                {name}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", lineHeight: 1.3 }}
              >
                {record.memberEmail}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        title: <><PhoneOutlined style={{ fontSize: 12 }} /> Phone</>,
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        width: 140,
        render: (phone: string, record: IMember) => (
          <Space size={4}>
            {phone ? (
              <Text style={{ fontSize: 13 }}>{phone}</Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>--</Text>
            )}
            <Tooltip title="Edit phone">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ fontSize: 12 }} />}
                onClick={() => openPhoneModal(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Joined</>,
        dataIndex: "createdAt",
        key: "createdAt",
        width: 140,
        sorter: (a: IMember, b: IMember) =>
          moment(a.createdAt).unix() - moment(b.createdAt).unix(),
        render: (createdAt: string) => (
          <div>
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {moment(createdAt).format("MMM D, YYYY")}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {moment(createdAt).fromNow()}
            </Text>
          </div>
        ),
      },
      {
        title: <><CheckCircleOutlined style={{ fontSize: 12 }} /> Status</>,
        dataIndex: "isActive",
        key: "isActive",
        width: 110,
        align: "center" as const,
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value: any, record: IMember) => record.isActive === value,
        render: (isActive: boolean, record: IMember) => (
          <Space direction="vertical" size={2} style={{ lineHeight: 1 }}>
            <Tag
              color={isActive ? "success" : "default"}
              icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            >
              {isActive ? "Active" : "Inactive"}
            </Tag>
            {record.isVerified && (
              <Tag
                color="blue"
                icon={<SafetyCertificateOutlined />}
                style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}
              >
                Verified
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: <><SafetyOutlined style={{ fontSize: 12 }} /> Role</>,
        dataIndex: "role",
        key: "role",
        width: 100,
        align: "center" as const,
        filters: [
          { text: "Admin", value: "admin" },
          { text: "User", value: "user" },
        ],
        onFilter: (value: any, record: IMember) => record.role === value,
        render: (role: string) => (
          <Tag
            color={role === "admin" ? "purple" : "blue"}
            icon={role === "admin" ? <CrownOutlined /> : <UserOutlined />}
          >
            {role.toUpperCase()}
          </Tag>
        ),
      },
      {
        title: <><SettingOutlined style={{ fontSize: 12 }} /> Actions</>,
        key: "actions",
        width: 120,
        align: "center" as const,
        render: (_: any, record: IMember) => (
          <Space size={4}>
            <Tooltip title={`Switch to ${record.role === "admin" ? "User" : "Admin"}`}>
              <Button
                type="text"
                size="small"
                icon={<SwapOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: "Change Role",
                    content: `Change ${record.memberName}'s role to ${
                      record.role === "admin" ? "USER" : "ADMIN"
                    }?`,
                    okText: "Confirm",
                    onOk: () =>
                      handleToggleRole(
                        record.memberId,
                        record.role === "admin" ? "user" : "admin"
                      ),
                  })
                }
              />
            </Tooltip>
            {record.isActive ? (
              <Tooltip title="Delete member">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    Modal.confirm({
                      title: "Delete Member",
                      content: `Are you sure you want to delete ${record.memberName}?`,
                      okText: "Delete",
                      okType: "danger",
                      onOk: () => handleDelete(record.memberId),
                    })
                  }
                />
              </Tooltip>
            ) : (
              <Tooltip title="Enable member">
                <Button
                  type="text"
                  size="small"
                  icon={<UnlockOutlined style={{ color: "#10b981" }} />}
                  onClick={() =>
                    Modal.confirm({
                      title: "Enable Member",
                      content: `Re-enable ${record.memberName}'s account?`,
                      okText: "Enable",
                      onOk: () => handleEnable(record.memberId),
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [getColumnSearchProps, handleDelete, handleEnable, handleToggleRole, openPhoneModal]
  );

  return (
    <div>
      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <TeamOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Team Members
          </Title>
          <Text className="page-subtitle">
            Manage users, roles, and access permissions
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setIsInviteModalOpen(true)}
          >
            Invite Member
          </Button>
        </Col>
      </Row>

      {/* Stat cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={12} sm={12} md={6}>
            <Card
              className="stat-card"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
              }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.1,
                    }}
                  >
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Toolbar + Table */}
      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search members..."
              style={{ width: 260 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table
          rowKey="memberId"
          size="middle"
          columns={columns}
          dataSource={filteredData}
          loading={{
            spinning: loading,
            tip: "Loading members...",
          }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0" }}>
                <UsergroupDeleteOutlined
                  style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                />
                <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                  No members found
                </div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                  {globalSearch
                    ? "Try adjusting your search"
                    : "Invite a member to get started"}
                </div>
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => (
              <Text style={{ color: "#64748b", fontSize: 13 }}>
                Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                <Text strong>{total}</Text> member{total !== 1 ? "s" : ""}
              </Text>
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {MemberInviteComponent(
        inviteForm,
        isInviteModalOpen,
        setIsInviteModalOpen,
        fetchMembers
      )}

      <Modal
        title={
          <>
            <PhoneOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Update Phone Number
          </>
        }
        open={phoneModalOpen}
        onCancel={() => {
          setPhoneModalOpen(false);
          setEditingMember(null);
          phoneForm.resetFields();
        }}
        onOk={handleUpdatePhone}
        okText="Update"
        confirmLoading={phoneLoading}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {editingMember?.memberName} — {editingMember?.memberEmail}
        </Text>
        <Form form={phoneForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: "Please enter a phone number" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\d{10}$/.test(value.trim())) return Promise.resolve();
                  return Promise.reject(
                    new Error("Phone number must be exactly 10 digits")
                  );
                },
              },
            ]}
          >
            <Input
              prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
              placeholder="9876543210"
              maxLength={10}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MemberTableComponent;
